/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { useServiceContext } from '../../root'
import { useModal } from '../../contexts/ModalContext'
import ActionsMenuBig from '../../components/shared/ActionsMenuBig'
import SaveAndShareReportModal from '../../components/reports/SaveAndShareReportModal'
import { SaveFilterType } from '../../constants/reportTypes'
import { useSearchService } from '../../hooks/useSearchService'

const LOADING_MENU_PLACEHOLDER = {
  value: 'Loading...',
  disabled: true
}

interface PageMenuProps {
  // No props needed as it reads from URL
}

export const PageMenu: React.FC<PageMenuProps> = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { showModal, hideModal } = useModal()
  const {
    service: {
      filter: { translate },
      auth,
      toast
    },
    api
  } = useServiceContext()
  const { assembleFiltersFromFormikValues } = useSearchService()

  const [items, setItems] = useState([LOADING_MENU_PLACEHOLDER])
  const [modalState, setModalState] = useState<{
    isOpen: boolean
    isEdit: boolean
    item?: any
  }>({
    isOpen: false,
    isEdit: false,
    item: undefined
  })

  useEffect(() => {
    loadMenuItems()
  }, [])

  const loadMenuItems = async () => {
    const menuItems: any[] = [
      {
        value: 'SAVED_FILTERS_SAVE_SEARCH_H_TITLE',
        action: () => {
          setModalState({
            isOpen: true,
            isEdit: false,
            item: undefined
          })
        }
      }
    ]

    try {
      const savedFilters = await api.formFilters.getAllSavedReportsFilters({
        pageSize: 5,
        pageStart: 0,
        sortBy: 'createdAt',
        sortDesc: true,
        filterTypes: SaveFilterType.SEARCH
      })

      if (savedFilters?.elements?.length > 0) {
        menuItems.push({ divider: true })
        menuItems.push({
          value: 'MY_SAVED_SEARCH_SETTINGS_TITLE',
          disabled: true
        })

        savedFilters.elements.forEach((filter: any) => {
          const submenu: any[] = [
            {
              value: 'SAVED_FILTERS_ACTION_RUN_SEARCH',
              action: async () => {
                try {
                  // Load the saved filter to get query parameters
                  const savedFilterData = await api.formFilters.getById(filter.id)
                  const filterObj =
                    typeof savedFilterData.filter === 'string'
                      ? JSON.parse(savedFilterData.filter)
                      : savedFilterData.filter

                  // Extract query and type from saved filter
                  const query = filterObj.phrase || ''
                  const searchType = filterObj.searchType || 'filename'

                  // Navigate to search page with filter data
                  const params = new URLSearchParams()
                  params.set('query', query)
                  params.set('type', searchType)
                  params.set('filter', filter.id.toString())
                  params.set('show', 'all')
                  navigate(`/search?${params.toString()}`)
                } catch (error) {
                  console.error('Failed to load saved filter:', error)
                  toast.error('Failed to load saved search')
                }
              }
            }
          ]

          // If user is owner, show Edit and Remove actions
          if (auth.getUserId() === filter.ownerId) {
            submenu.push(
              {
                value: 'SAVED_FILTERS_ACTION_EDIT',
                action: () => {
                  setModalState({
                    isOpen: true,
                    isEdit: true,
                    item: filter
                  })
                }
              },
              {
                value: 'ACTION_REMOVE',
                action: async () => {
                  try {
                    await api.formFilters.deleteFilter(
                      filter.id,
                      'searches',
                      filter,
                      undefined,
                      { showModal, hideModal },
                      toast,
                      translate,
                      loadMenuItems
                    )
                  } catch (error: any) {
                    // Error already handled by service (toast shown)
                    // Only log if not user cancellation
                    if (error.message !== 'User cancelled') {
                      console.error('Delete filter error:', error)
                    }
                  }
                }
              }
            )
          } else {
            // If user is not owner, show Save As action
            submenu.push({
              value: 'SAVED_FILTERS_ACTION_SAVE_AS',
              action: () => {
                // For "Save As", we open the save modal with the filter's data pre-populated
                // but treat it as a new save (not edit mode)
                setModalState({
                  isOpen: true,
                  isEdit: false,
                  item: filter // Pass filter to pre-populate fields
                })
              }
            })
          }

          menuItems.push({
            value: filter.name,
            submenu
          })
        })

        menuItems.push({
          value: 'MORE...',
          action: () => {
            navigate('/MySavedReportSettings?from=savedSearchPage')
          }
        })
      }
    } catch (error) {
      console.error('Failed to load saved searches:', error)
    }

    setItems(menuItems)
  }

  const handleModalClose = () => {
    setModalState({
      isOpen: false,
      isEdit: false,
      item: undefined
    })
    // Reload menu items after save/edit
    loadMenuItems()
  }

  // Create a filterAssembler that reads from URL params AND localStorage
  // This ensures all filter fields (dates, doc type, state, etc.) are saved
  const getFilterDataFromUrl = () => {
    const query = searchParams.get('query') || ''
    const type = searchParams.get('type') || 'filename'
    const show = searchParams.get('show') || 'all'

    // Start with basic values from URL
    let filterValues: any = {
      phrase: decodeURIComponent(query),
      searchType: show !== 'all' ? show : type
    }

    // Read the complete filter from localStorage where SearchTableFilter persists it
    // This includes: lastUpdatedType, updatedFrom, updatedTo, state, itemType, docType
    try {
      const persistedFilter = localStorage.getItem('searchPage_filter')
      if (persistedFilter) {
        const parsed = JSON.parse(persistedFilter)
        // Merge persisted filter with URL params
        // URL params (phrase, searchType) take precedence as they're more current
        filterValues = {
          ...parsed,
          phrase: decodeURIComponent(query) || parsed.phrase || '',
          searchType: show !== 'all' ? show : type || parsed.searchType || 'filename'
        }
      }
    } catch (e) {
      console.error('Failed to load persisted filter from localStorage:', e)
    }

    return assembleFiltersFromFormikValues(filterValues, true)
  }

  return (
    <div>
      <ActionsMenuBig menuId="page" items={items} />

      {modalState.isOpen && (
        <SaveAndShareReportModal
          type={SaveFilterType.SEARCH}
          filterAssembler={getFilterDataFromUrl}
          onClose={handleModalClose}
          isEdit={modalState.isEdit}
          item={modalState.item}
          isHideScheduleSettings
        />
      )}
    </div>
  )
}

export default PageMenu
